import type { TimedCheck } from '../domain/catalog';
import styles from './CheckList.module.css';
import { cx } from './cx';

interface CheckListProps {
  readonly checks: readonly TimedCheck[];
  /** Seconds into the phase; each check passes once this reaches its `at`. */
  readonly elapsed: number;
  /** How far ahead of passing a row brightens, in seconds. */
  readonly lookahead: number;
  readonly variant: 'boot' | 'link';
  /** Show animated dots instead of "· · ·" on the final pending row. */
  readonly busyLastRow?: boolean;
  readonly className?: string | undefined;
}

/** A self-test sequence: rows flip from pending to "[ OK ]" as the phase timer passes them. */
export function CheckList({
  checks,
  elapsed,
  lookahead,
  variant,
  busyLastRow = false,
  className,
}: CheckListProps) {
  return (
    <ul className={cx(styles.card, styles[variant], className)}>
      {checks.map((check, i) => {
        const done = elapsed >= check.at;
        const busy = busyLastRow && !done && i === checks.length - 1;
        return (
          <li
            key={check.label}
            className={cx(
              styles.row,
              done && styles.done,
              elapsed < check.at - lookahead && styles.upcoming,
            )}
          >
            <span className={styles.text}>
              <span className={styles.label}>{check.label}</span>
              {check.detail && <span className={styles.detail}>{check.detail}</span>}
            </span>
            {busy ? (
              <span className={styles.dots} aria-label="In progress">
                <span className={styles.dot} />
                <span className={styles.dot} />
                <span className={styles.dot} />
              </span>
            ) : (
              <span className={styles.status}>{done ? '[ OK ]' : '· · ·'}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
